import { makeStyles } from '@material-ui/core/styles'

import { MAX_ATTACHMENTS_PER_MESSAGE } from '../../core/utils/attachment'

type AnalyzeEmptyStateProps = {
  onStartCompare?: () => void
}

const useStyles = makeStyles(() => ({
  root: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: 'var(--ds-bg-subtle, #f1f2f7)',
    padding: '40px 24px',
    height: '100%',
  },
  inner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
    maxWidth: 760,
  },
  iconHost: {
    marginBottom: 28,
    width: 76,
    height: 76,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 22,
    border: '1px solid rgba(15,23,42,0.05)',
    background: 'oklch(79.011% 0.04559 259)',
    boxShadow: '0 10px 24px rgba(15,23,42,0.06)',
  },
  emoji: {
    fontSize: 34,
    lineHeight: 1,
  },
  titleBlock: {
    marginBottom: 40,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    maxWidth: 520,
  },
  title: {
    fontSize: '1.75rem',
    lineHeight: 1.2,
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: 'var(--ds-fg-primary, #1f2430)',
    margin: 0,
  },
  body: {
    fontSize: '0.9375rem',
    lineHeight: 2,
    fontWeight: 400,
    color: 'var(--ds-fg-secondary, #535b6c)',
    margin: 0,
  },
  steps: {
    marginBottom: 40,
    display: 'flex',
    width: '100%',
    maxWidth: 360,
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  step: {
    width: 96,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    textAlign: 'center',
  },
  stepNumber: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    background: 'oklch(79.011% 0.04559 259)',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'oklch(14.781% 0.0225 237.71)',
    boxShadow: '0 0 20px rgba(239,244,255,0.7)',
  },
  stepLabel: {
    fontSize: '0.875rem',
    lineHeight: 1.25,
    fontWeight: 400,
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    margin: 0,
  },
  startButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 14,
    background: 'oklch(79.011% 0.04559 259)',
    padding: '16px 56px',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'oklch(14.781% 0.0225 237.71)',
    border: 0,
    cursor: 'pointer',
    boxShadow: '0 0 20px rgba(239,244,255,0.7)',
    transition: 'transform 160ms ease, box-shadow 220ms ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 28px rgba(239,244,255,0.95)',
    },
  },
}))

const steps = [
  { number: 1, label: 'Select doc type' },
  { number: 2, label: 'Upload files' },
  { number: 3, label: 'Get insights' },
]

const AnalyzeEmptyState = ({ onStartCompare }: AnalyzeEmptyStateProps) => {
  const classes = useStyles()

  return (
    <div className={classes.root}>
      <div className={classes.inner}>
        <div className={classes.iconHost}>
          <span className={classes.emoji} aria-hidden="true">📊</span>
        </div>

        <div className={classes.titleBlock}>
          <h2 className={classes.title}>Start Analyze Flow</h2>
          <p className={classes.body}>
            Compare and analyze up to {MAX_ATTACHMENTS_PER_MESSAGE} documents at the same time. I can
            help you extract insights, compare budgets, and identify key differences.
          </p>
        </div>

        <div className={classes.steps}>
          {steps.map((step) => (
            <div key={step.number} className={classes.step}>
              <div className={classes.stepNumber}>{step.number}</div>
              <p className={classes.stepLabel}>{step.label}</p>
            </div>
          ))}
        </div>

        <button
          type="button"
          className={classes.startButton}
          onClick={onStartCompare}
        >
          Get Started
        </button>
      </div>
    </div>
  )
}

export default AnalyzeEmptyState
